import { getTrainingLogsForProcedureRevision } from "../redux/trainingLogSlice";
import { parseRevision, revisionSort } from "../pages/Documents/Utils/utils.js";
import { getCurrentStatus } from "./AppUtils.jsx";

/** Adds `part_ref` to GET training-log/get-all when the part id is known (backend expects part UUID). */
function withPartRefParams(base, partRef) {
  if (partRef == null || String(partRef).trim() === "") return base;
  return { ...base, part_ref: String(partRef).trim() };
}

const normalizeTrainedTo = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === "object" && parsed !== null ? parsed : null;
    } catch {
      return null;
    }
  }
  if (typeof raw === "object") return raw;
  return null;
};

/**
 * POST /training-log/create may send `trained_to` as `{ tree_node_id, data: { selection_scope, steps, ... } }`.
 * Older rows store `{ selection_scope, steps, ... }` at the top level — return that object in both cases.
 */
export function unwrapTrainedToPayload(raw) {
  const tt = normalizeTrainedTo(raw);
  if (!tt || typeof tt !== "object") return null;
  const inner = tt.data;
  if (
    inner &&
    typeof inner === "object" &&
    (Array.isArray(inner.steps) || inner.selection_scope != null)
  ) {
    return inner;
  }
  return tt;
}

const hasUsableTrainedTo = (log) => {
  const tt = unwrapTrainedToPayload(log?.trained_to);
  if (!tt || typeof tt !== "object" || Object.keys(tt).length === 0) {
    return false;
  }
  if (tt.selection_scope === "procedure") return true;
  return Array.isArray(tt.steps) && tt.steps.length > 0;
};

/** GET training-log row: API marks workflow / full completion (Submitter, Approver, Releaser, etc.). */
export function isTopLevelTrainingLogComplete(log) {
  if (!log || typeof log !== "object") return false;
  const v = log.is_complete;
  if (v === true || v === 1) return true;
  if (typeof v === "string" && v.trim().toLowerCase() === "true") return true;
  return false;
}

function normalizeTraineeIdPlain(id) {
  if (id == null || id === "") return "";
  return String(id).trim().toLowerCase();
}

/**
 * Restrict GET results to the trainee we asked for (backend may still return mixed rows).
 */
function trainingLogsForTraineeId(sorted, traineeId) {
  if (!Array.isArray(sorted) || sorted.length === 0) return [];
  if (traineeId == null || String(traineeId).trim() === "") return sorted;
  const want = normalizeTraineeIdPlain(traineeId);
  return sorted.filter((row) => {
    const raw =
      row?.trainee?.id ??
      row?.trainee?.user_id ??
      row?.trainee?.user_ref ??
      row?.trainee;
    if (raw == null) return false;
    return normalizeTraineeIdPlain(raw) === want;
  });
}

/**
 * Some GET /training-log/get-all rows reuse `procedure_revision_ref` as `training_log_id` with empty
 * `trained_to` and `is_complete: true` (workflow stubs). They must not win over real logs with
 * `trained_to.data` (e.g. Add Module "Table of Procedures" / Document status).
 */
function isSpuriousRevisionKeyedTrainingLogRow(log) {
  if (!log || typeof log !== "object") return false;
  const logId =
    log.training_log_id ?? log.id ?? log.trainingLogId ?? null;
  const revRef =
    log.procedure_revision_ref ??
    log.procedure_revision_id ??
    log.procedure_rev_ref ??
    null;
  if (logId == null || revRef == null) return false;
  return (
    normalizeProcedureRevisionIdKey(logId) ===
    normalizeProcedureRevisionIdKey(revRef)
  );
}

/** Drop spurious stub rows when the API also returns real training logs. */
function trainingLogsEligibleForStatusLabel(sorted) {
  if (!Array.isArray(sorted) || sorted.length === 0) return [];
  const filtered = sorted.filter((l) => !isSpuriousRevisionKeyedTrainingLogRow(l));
  return filtered.length ? filtered : sorted;
}

/** Prefer client-side trainee filter; if no row exposes `trainee`, trust GET ?trainee=… response. */
function poolTrainingLogsForTrainee(sorted, traineeId) {
  const scoped = trainingLogsForTraineeId(sorted, traineeId);
  if (scoped.length) return scoped;
  if (traineeId == null || String(traineeId).trim() === "") return sorted;
  const anyTraineeField = sorted.some((row) => {
    const raw =
      row?.trainee?.id ??
      row?.trainee?.user_id ??
      row?.trainee?.user_ref ??
      row?.trainee;
    return raw != null;
  });
  if (!anyTraineeField && sorted.length) return sorted;
  return [];
}

/**
 * When GET returns multiple rows for one trainee, prefer one the API flagged complete, then step payload.
 * (Otherwise `find(hasUsableTrainedTo)` can skip the `is_complete` + empty `trained_to` row.)
 */
function pickTrainingLogRowForStatusLabel(sorted) {
  if (!Array.isArray(sorted) || sorted.length === 0) return null;
  const pool = trainingLogsEligibleForStatusLabel(sorted);
  const complete = pool.find((l) => isTopLevelTrainingLogComplete(l));
  if (complete) return complete;
  const withPayload = pool.find(hasUsableTrainedTo);
  if (withPayload) return withPayload;
  return pool[0];
}

/**
 * Per-step "Trained" / checkbox pre-check must read `trained_to.steps`. Prefer a row that actually
 * has that payload over a workflow stub with `is_complete: true` and empty `trained_to` (otherwise
 * every step shows "Not Trained" even when a prior incomplete log lists trained steps).
 */
function pickTrainingLogRowForPerStepDerivation(sorted) {
  if (!Array.isArray(sorted) || sorted.length === 0) return null;
  const pool = trainingLogsEligibleForStatusLabel(sorted);
  const withPayload = pool.find(hasUsableTrainedTo);
  if (withPayload) return withPayload;
  const complete = pool.find((l) => isTopLevelTrainingLogComplete(l));
  if (complete) return complete;
  return pool[0];
}

/**
 * When trainee is not specified (mixed users in one response), do not prefer `is_complete` first —
 * that would apply another user's completion to everyone.
 */
function pickTrainingLogRowForStatusLabelUnscoped(sorted) {
  if (!Array.isArray(sorted) || sorted.length === 0) return null;
  const pool = trainingLogsEligibleForStatusLabel(sorted);
  const withPayload = pool.find(hasUsableTrainedTo);
  if (withPayload) return withPayload;
  return pool[0];
}

const normStepStatus = (s) => String(s ?? "").trim().toLowerCase();

const computeTrainingStatusFromLatestLog = (latestLog) => {
  const trainedTo = unwrapTrainedToPayload(latestLog?.trained_to);
  if (!trainedTo || typeof trainedTo !== "object" || Object.keys(trainedTo).length === 0) {
    return "Not Trained";
  }

  /** Full procedure: never treat as fully Trained if any step is still Partially Trained — even when
   * `data.is_complete` is true (API can set both; same-letter inheritance must not upgrade partial rows). */
  if (trainedTo.selection_scope === "procedure") {
    const stepsAll = Array.isArray(trainedTo.steps) ? trainedTo.steps : [];
    const anyPartial = stepsAll.some(
      (s) =>
        s &&
        typeof s === "object" &&
        normStepStatus(s.status) === "partially trained",
    );
    if (anyPartial) return "Partially Trained";
    if (trainedTo.is_complete === true) {
      return "Trained";
    }
    if (stepsAll.length === 0) return "Trained";
    const allOk = stepsAll.every(
      (s) =>
        normStepStatus(s?.status) === "trained" ||
        s?.is_complete === true,
    );
    if (allOk) return "Trained";
    return "Not Trained";
  }

  const steps = Array.isArray(trainedTo.steps) ? trainedTo.steps : [];
  // New payloads include every step with is_complete; omit rows not completed for this log.
  const stepsForStatus = steps.filter(
    (s) => s != null && typeof s === "object" && s.is_complete !== false,
  );

  if (!stepsForStatus.length) {
    return "Not Trained";
  }

  const normalizedSteps = stepsForStatus.map((s, idx) => {
    const rawOrder =
      s?.sibling_order ??
      s?.detail?.sibling_order ??
      s?.detail?.step_number ??
      s?.detail?.step_no;
    const parsedOrder = Number.parseInt(rawOrder, 10);
    const stepNumber = Number.isFinite(parsedOrder) ? parsedOrder : idx + 1;
    return { status: s?.status, stepNumber };
  });

  const allTrained = normalizedSteps.every((s) => normStepStatus(s.status) === "trained");
  if (allTrained) return "Trained";

  const hasPartial = normalizedSteps.some(
    (s) => normStepStatus(s.status) === "partially trained",
  );
  if (hasPartial) {
    return "Partially Trained";
  }

  return "Not Trained";
};

/** Stable map key for procedure revision UUIDs (avoids casing mismatches vs API / row data). */
export const normalizeProcedureRevisionIdKey = (id) => {
  if (id == null || id === "") return "";
  return String(id).trim().toLowerCase();
};

/** Same normalization for step / tree node ids (GET logs may use different fields than live nodes). */
const normalizeTreeNodeIdKey = normalizeProcedureRevisionIdKey;

/**
 * Training log `trained_to.steps[]` rows may use `tree_node_id`, `detail.step_tree_node_id`, or `step_id_ref`.
 * Live revision nodes use `step.tree_node_id` (often equals `step_tree_node_id` from the API, not the root `tree_node_id`).
 */
export function collectCandidateStepIdsFromTrainingLogStep(stepRow) {
  const ids = [];
  const push = (v) => {
    if (v != null && String(v).trim() !== "") ids.push(String(v).trim());
  };
  if (!stepRow || typeof stepRow !== "object") return ids;
  push(stepRow.tree_node_id);
  push(stepRow.step_id_ref);
  const d = stepRow.detail;
  if (d && typeof d === "object") {
    push(d.step_tree_node_id);
    push(d.step_id_ref);
    /** GET logs sometimes align `tree_node_id` with procedure tree ref, not `step.tree_node_id`. */
    push(d.procedure_tree_ref);
  }
  return ids;
}

/**
 * POST / GET may store `trained_to` as `{ data: { steps, selection_scope }, tree_node_id }` where
 * `tree_node_id` is the **current** procedure-tree step id (matches get-procedure-nodes), while each
 * `steps[]` row may still reference **older** ids after amend/release. Matching must include this id
 * for the first step row (see derivePerStepTrainingSetsFromLog).
 */
function getTrainedToWrapperTreeNodeId(latestLog) {
  const raw = normalizeTrainedTo(latestLog?.trained_to);
  if (!raw || typeof raw !== "object") return "";
  const id = raw.tree_node_id;
  if (id == null || String(id).trim() === "") return "";
  return String(id).trim();
}

function sortSectionsLikeDocument(allNodesData) {
  if (!Array.isArray(allNodesData)) return [];
  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    if (Number.isNaN(intA) && Number.isNaN(intB)) return 0;
    if (Number.isNaN(intA)) return 1;
    if (Number.isNaN(intB)) return -1;
    return intA - intB;
  };
  return [...allNodesData].sort((a, b) => {
    const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
    const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
    const orderCompare = compareSiblingOrders(orderA, orderB);
    if (orderCompare !== 0) return orderCompare;
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return dateB - dateA;
  });
}

/**
 * Maps latest training log `trained_to` to global step numbers (same ordering as Document / RenderRevisionNodes).
 * @returns {{ allStepsTrained: boolean, fullyTrained: Set<number>, partial: Set<number> }}
 */
export function derivePerStepTrainingSetsFromLog(latestLog, allNodesData) {
  const fullyTrained = new Set();
  const partial = new Set();

  if (!latestLog || typeof latestLog !== "object") {
    return { allStepsTrained: false, fullyTrained, partial };
  }

  const trainedTo = unwrapTrainedToPayload(latestLog.trained_to);
  if (!trainedTo || typeof trainedTo !== "object") {
    return { allStepsTrained: false, fullyTrained, partial };
  }

  if (trainedTo.selection_scope === "procedure" && trainedTo.is_complete === true) {
    return { allStepsTrained: true, fullyTrained, partial };
  }

  const steps = Array.isArray(trainedTo.steps) ? trainedTo.steps : [];

  if (trainedTo.selection_scope === "procedure" && steps.length === 0) {
    return { allStepsTrained: true, fullyTrained, partial };
  }

  if (steps.length === 0) {
    const label = resolveTrainingStatusLabelFromLog(latestLog);
    if (String(label).trim().toLowerCase() === "trained") {
      return { allStepsTrained: true, fullyTrained, partial };
    }
    return { allStepsTrained: false, fullyTrained, partial };
  }

  const sortedSections = sortSectionsLikeDocument(allNodesData);
  const idToGlobal = new Map();
  const siblingOrderToGlobal = new Map();
  /** Map section UUID (from log `section_id_ref`) → sectionKey used in siblingOrderToGlobal compound keys. */
  const sectionIdToKey = new Map();
  let globalStepNumber = 0;
  for (let si = 0; si < sortedSections.length; si += 1) {
    const section = sortedSections[si];
    const sectionSteps = Array.isArray(section.steps) ? section.steps : [];
    const sectionOrderRaw =
      section?.sibling_order ?? section?.procedure_tree?.sibling_order ?? "";
    const sectionOrderParsed = Number.parseInt(sectionOrderRaw, 10);
    const sectionKey = Number.isFinite(sectionOrderParsed)
      ? String(sectionOrderParsed)
      : `idx${si}`;
    const registerSectionId = (id) => {
      if (id == null || String(id).trim() === "") return;
      const k = normalizeTreeNodeIdKey(id);
      if (k) sectionIdToKey.set(k, sectionKey);
    };
    registerSectionId(section?.section_id_ref);
    registerSectionId(section?.section_id);
    registerSectionId(section?.tree_node_id);
    registerSectionId(section?.procedure_tree?.tree_node_id);

    for (const step of sectionSteps) {
      globalStepNumber += 1;
      const stepSiblingOrder =
        step?.sibling_order ??
        step?.detail?.sibling_order ??
        step?.detail?.step_number ??
        step?.detail?.step_no;
      const stepSiblingOrderParsed = Number.parseInt(stepSiblingOrder, 10);
      if (Number.isFinite(stepSiblingOrderParsed) && stepSiblingOrderParsed > 0) {
        siblingOrderToGlobal.set(
          `${sectionKey}:${stepSiblingOrderParsed}`,
          globalStepNumber,
        );
        siblingOrderToGlobal.set(stepSiblingOrderParsed, globalStepNumber);
      }
      const candidates = [
        step.tree_node_id,
        step.step_id_ref,
        step.detail?.step_tree_node_id,
        step.detail?.procedure_tree_ref,
      ].filter(Boolean);
      for (const id of candidates) {
        idToGlobal.set(normalizeTreeNodeIdKey(id), globalStepNumber);
      }
    }
  }

  const totalStepsInRevision = globalStepNumber;
  const stepsAlignWithProcedure =
    totalStepsInRevision > 0 && steps.length === totalStepsInRevision;

  const wrapperTreeNodeId = getTrainedToWrapperTreeNodeId(latestLog);

  for (let stepIdx = 0; stepIdx < steps.length; stepIdx += 1) {
    const row = steps[stepIdx];
    if (row && row.is_complete === false) continue;
    const statusRaw = String(row?.status ?? "")
      .trim()
      .toLowerCase();
    const candidates = collectCandidateStepIdsFromTrainingLogStep(row);
    /**
     * First step in procedure order: include wrapper `trained_to.tree_node_id` so it matches live
     * `step.tree_node_id` from get-procedure-nodes when inner rows still carry pre-amend ids.
     * Prefer `sibling_order === "1"`; fall back to first array index when order is missing.
     */
    const siblingOrder = row?.sibling_order != null ? String(row.sibling_order).trim() : "";
    if (
      wrapperTreeNodeId &&
      (siblingOrder === "1" || (stepIdx === 0 && siblingOrder === ""))
    ) {
      candidates.push(wrapperTreeNodeId);
    }
    let globalNum = null;
    /**
     * GET payloads (see `trained_to.data.steps`) are a full ordered list: one entry per procedure step.
     * On a **new revision**, `tree_node_id` / `procedure_tree_ref` often still point at the **old**
     * tree but may **collide** with unrelated nodes on the new tree — ID lookup then maps to the wrong
     * global step. When `steps.length` matches the live procedure step count, **array index** is the
     * authoritative global step number (same contract as POST /training-log/create).
     */
    if (stepsAlignWithProcedure) {
      globalNum = stepIdx + 1;
    } else {
      for (const id of candidates) {
        const g = idToGlobal.get(normalizeTreeNodeIdKey(id));
        if (g != null) {
          globalNum = g;
          break;
        }
      }
      if (globalNum == null) {
        const rawOrder =
          row?.sibling_order ??
          row?.detail?.sibling_order ??
          row?.detail?.step_number ??
          row?.detail?.step_no;
        const parsedOrder = Number.parseInt(rawOrder, 10);
        if (Number.isFinite(parsedOrder) && parsedOrder > 0) {
          let byOrder = siblingOrderToGlobal.get(parsedOrder);
          if (byOrder == null && row?.detail && typeof row.detail === "object") {
            const secRaw =
              row.detail.section_sibling_order ??
              row.detail.section_order ??
              row.detail.sectionSiblingOrder;
            const secParsed = Number.parseInt(secRaw, 10);
            if (Number.isFinite(secParsed)) {
              byOrder = siblingOrderToGlobal.get(`${secParsed}:${parsedOrder}`);
            }
          }
          if (byOrder == null && row?.section_id_ref != null) {
            const secKey = sectionIdToKey.get(
              normalizeTreeNodeIdKey(row.section_id_ref),
            );
            if (secKey) {
              byOrder = siblingOrderToGlobal.get(`${secKey}:${parsedOrder}`);
            }
          }
          if (byOrder != null) globalNum = byOrder;
        }
      }
    }
    if (globalNum == null) continue;
    if (statusRaw === "trained") {
      fullyTrained.add(globalNum);
    } else if (statusRaw === "partially trained") {
      partial.add(globalNum);
    } else if (
      row &&
      (row.is_complete === true ||
        row.is_complete === 1 ||
        String(row.is_complete ?? "")
          .trim()
          .toLowerCase() === "true")
    ) {
      /** Status missing or nonstandard — step still marked complete in log. */
      partial.add(globalNum);
    }
  }

  return { allStepsTrained: false, fullyTrained, partial };
}

async function fetchSortedTrainingLogsForProcedureRevision(
  dispatch,
  procedureRevisionId,
  partRef,
) {
  const key = normalizeProcedureRevisionIdKey(procedureRevisionId);
  if (!key) return { key: "", sorted: [] };

  const payload = await dispatch(
    getTrainingLogsForProcedureRevision(
      withPartRefParams(
        {
          procedureRevisionId: key,
          limit: 100,
          offset: 0,
        },
        partRef,
      ),
    ),
  ).unwrap();

  const results =
    (payload && Array.isArray(payload.results) && payload.results) ||
    (payload && Array.isArray(payload.data) && payload.data) ||
    (Array.isArray(payload) ? payload : []);

  const sorted = [...results].sort((a, b) => {
    // Many rows share the same date_of_training (midnight UTC). Break ties using created/updated.
    const aTrain = a?.date_of_training || "";
    const bTrain = b?.date_of_training || "";
    const trainDiff = new Date(bTrain).getTime() - new Date(aTrain).getTime();
    if (Number.isFinite(trainDiff) && trainDiff !== 0) return trainDiff;

    const aCreated = a?.created_at || a?.updated_at || "";
    const bCreated = b?.created_at || b?.updated_at || "";
    const createdDiff =
      new Date(bCreated).getTime() - new Date(aCreated).getTime();
    if (Number.isFinite(createdDiff) && createdDiff !== 0) return createdDiff;

    // Final fallback: keep stable order.
    return 0;
  });

  return { key, sorted };
}

async function fetchSortedTrainingLogsForProcedureRevisionForTrainee(
  dispatch,
  procedureRevisionId,
  traineeId,
  partRef,
) {
  void traineeId;
  // GET training-log/get-all without `trainee` (procedure_revision_ref, limit, offset, part_ref only).
  // Callers scope by trainee via `poolTrainingLogsForTrainee` / `trainingLogsForTraineeId`.
  return fetchSortedTrainingLogsForProcedureRevision(
    dispatch,
    procedureRevisionId,
    partRef,
  );
}

/**
 * Latest log row for the revision (for `trained_to` parsing). Same sort as status label.
 */
export async function fetchLatestTrainingLogForProcedureRevision(
  dispatch,
  procedureRevisionId,
  partRef,
) {
  try {
    const { key, sorted } = await fetchSortedTrainingLogsForProcedureRevision(
      dispatch,
      procedureRevisionId,
      partRef,
    );
    if (!sorted.length) {
      return { procedureRevisionId: key, status: "Not Trained", log: null };
    }
    const logToUse = pickTrainingLogRowForStatusLabelUnscoped(sorted);
    const status = logToUse ? resolveTrainingStatusLabelFromLog(logToUse) : "Not Trained";
    return { procedureRevisionId: key, status: String(status), log: logToUse };
  } catch (e) {
    console.error(
      "Failed to fetch latest training log for procedureRevisionId:",
      procedureRevisionId,
      e,
    );
    return {
      procedureRevisionId: normalizeProcedureRevisionIdKey(procedureRevisionId),
      status: "Not Trained",
      log: null,
    };
  }
}

export async function fetchLatestTrainingLogForProcedureRevisionForTrainee(
  dispatch,
  procedureRevisionId,
  traineeId,
  partRef,
) {
  try {
    const { key, sorted } = await fetchSortedTrainingLogsForProcedureRevisionForTrainee(
      dispatch,
      procedureRevisionId,
      traineeId,
      partRef,
    );
    if (!sorted.length) {
      return { procedureRevisionId: key, status: "Not Trained", log: null };
    }
    const pool = poolTrainingLogsForTrainee(sorted, traineeId);
    if (!pool.length) {
      return { procedureRevisionId: key, status: "Not Trained", log: null };
    }
    const logToUse = pickTrainingLogRowForPerStepDerivation(pool);
    const status = logToUse ? resolveTrainingStatusLabelFromLog(logToUse) : "Not Trained";
    return { procedureRevisionId: key, status: String(status), log: logToUse };
  } catch (e) {
    console.error(
      "Failed to fetch latest trainee training log for procedureRevisionId:",
      procedureRevisionId,
      traineeId,
      e,
    );
    return {
      procedureRevisionId: normalizeProcedureRevisionIdKey(procedureRevisionId),
      status: "Not Trained",
      log: null,
    };
  }
}

export const getProcedureRevisionLookupKeyFromRow = (row) =>
  normalizeProcedureRevisionIdKey(
    row?.procedureRevisionId ??
      row?.procedure_revision_id ??
      row?.procedure_revision_ref ??
      row?.procedure_rev_ref ??
      "",
  );

/** Collapse UUID strings that differ only by hyphens/casing (dedupe keys). */
function normalizeUuidLikeKey(value) {
  if (value == null || value === "") return "";
  const s = String(value).trim().toLowerCase();
  if (s.startsWith("email:") || s.startsWith("name:")) return s;
  const hex = s.replace(/-/g, "");
  if (/^[0-9a-f]{32}$/.test(hex)) return hex;
  return s;
}

function normalizeProcedureRevisionDedupeKey(row) {
  const fromFields = normalizeUuidLikeKey(getProcedureRevisionLookupKeyFromRow(row));
  if (fromFields) return fromFields;
  const revLabel =
    row?.rev ?? row?.version_id_ref ?? row?.revision_version ?? row?.revision;
  if (revLabel != null && String(revLabel).trim() !== "") {
    return `rev:${String(revLabel).trim().toLowerCase()}`;
  }
  return "";
}

/** Trainee id / refs only (no trainee_name) — stable key when API duplicates rows. */
function normalizeTraineeIdFromTrainingLogRowIdOnly(row) {
  const raw =
    row?.trainee?.id ??
    row?.trainee?.user_id ??
    row?.trainee?.user_ref ??
    row?.trainee_id ??
    row?.trainee_ref ??
    row?.user_ref ??
    (typeof row?.trainee === "string" ? row.trainee : null);
  if (raw == null || String(raw).trim() === "") return "";
  return String(raw).trim().toLowerCase();
}

/** Match GET row trainee to a workflow user id from `collectWorkflowActorUserIdsFromRevision` (hex-normalized). */
function trainingLogRowMatchesWorkflowUserId(row, uidLower) {
  if (!row || uidLower == null || String(uidLower).trim() === "") return false;
  const want = normalizeUuidLikeKey(String(uidLower).trim().toLowerCase());
  if (!want) return false;
  const idOnly = normalizeTraineeIdFromTrainingLogRowIdOnly(row);
  if (!idOnly) return false;
  return normalizeUuidLikeKey(idOnly) === want;
}

/** Display name for collapse when API sends two rows for the same person (mixed id / name fields). */
function normalizeTraineeDisplayNameForDedupe(row) {
  if (row?.trainee_name && String(row.trainee_name).trim()) {
    return String(row.trainee_name)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
  }
  const t = row?.trainee;
  if (t && typeof t === "object") {
    const name = `${t.first_name || ""} ${t.last_name || ""}`.trim();
    if (name) return name.toLowerCase().replace(/\s+/g, " ");
  }
  return "";
}

/**
 * Final pass: same procedure revision + same visible trainee name → one row (latest wins).
 * Collapses id-only vs name-only duplicates for the same person on the same revision.
 */
function collapseDuplicateTraineePerRevisionByDisplayName(rows, compareDesc) {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  const sorted = [...rows].sort(compareDesc);
  const seen = new Set();
  const out = [];
  for (const r of sorted) {
    const pr = normalizeProcedureRevisionDedupeKey(r);
    const displayName = normalizeTraineeDisplayNameForDedupe(r);
    if (!pr) {
      out.push(r);
      continue;
    }
    if (!displayName) {
      out.push(r);
      continue;
    }
    const k = `${pr}|${displayName}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(r);
  }
  return out;
}

/**
 * True if the logged-in user is Submitter, Approver, or Releaser for this revision
 * (`required_by` from the procedure revision API). Same rules as Document.js revisionTrainerOptions.
 */
export function isRevisionWorkflowActor(requiredByOrRevision, authUserId) {
  if (authUserId == null || authUserId === "") return false;
  const rb = Array.isArray(requiredByOrRevision)
    ? requiredByOrRevision
    : requiredByOrRevision?.required_by;
  if (!Array.isArray(rb)) return false;
  const aid = String(authUserId).trim().toLowerCase();
  for (const entry of rb) {
    const p = String(entry?.purpose ?? "").toLowerCase();
    if (p !== "submit" && p !== "approve" && p !== "release") continue;
    const u = entry?.user;
    if (!u) continue;
    const uid = u.id ?? u.user_id ?? u.user_ref;
    if (uid != null && String(uid).trim().toLowerCase() === aid) return true;
  }
  return false;
}

function normalizePersonNameKey(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/**
 * Same as `isRevisionWorkflowActor`, but matches a GET training-log **row** whose trainee may only
 * have `trainee_name` / email (no id). `normalizeTraineeIdFromTrainingLogRow` then yields `name:…`
 * or `email:…`, which plain id-only workflow checks miss.
 */
export function isRevisionWorkflowActorForTrainingRow(requiredByOrRevision, row) {
  if (!row || typeof row !== "object") return false;
  const tid = normalizeTraineeIdFromTrainingLogRow(row);
  if (tid && !tid.startsWith("name:") && !tid.startsWith("email:")) {
    return isRevisionWorkflowActor(requiredByOrRevision, tid);
  }

  const rb = Array.isArray(requiredByOrRevision)
    ? requiredByOrRevision
    : requiredByOrRevision?.required_by;
  if (!Array.isArray(rb)) return false;

  const trainee = row.trainee;
  const nameFromRow =
    (typeof row.trainee_name === "string" && row.trainee_name.trim()) ||
    (trainee && typeof trainee === "object"
      ? `${trainee.first_name || ""} ${trainee.last_name || ""}`.trim()
      : "");
  const emailFromRow =
    (trainee && typeof trainee === "object" && trainee.email) ||
    row.trainee_email ||
    row.email ||
    "";

  let wantName = "";
  let wantEmail = "";
  if (tid.startsWith("name:")) {
    wantName = normalizePersonNameKey(tid.slice("name:".length));
  } else if (tid.startsWith("email:")) {
    wantEmail = String(tid.slice("email:".length)).trim().toLowerCase();
  }

  for (const entry of rb) {
    const p = String(entry?.purpose ?? "").toLowerCase();
    if (p !== "submit" && p !== "approve" && p !== "release") continue;
    const u = entry?.user;
    if (!u) continue;
    const uName = normalizePersonNameKey(
      `${u.first_name || ""} ${u.last_name || ""}`.trim(),
    );
    const uEmail = String(u.email || "").trim().toLowerCase();
    if (wantName && uName && uName === wantName) return true;
    if (wantEmail && uEmail && uEmail === wantEmail) return true;
    if (nameFromRow && uName && normalizePersonNameKey(nameFromRow) === uName) return true;
    if (
      emailFromRow &&
      uEmail &&
      String(emailFromRow).trim().toLowerCase() === uEmail
    ) {
      return true;
    }
  }
  return false;
}

/**
 * User ids (lowercased) for final Submitter / Approver / Releaser slots on a revision (`required_by`).
 */
export function collectWorkflowActorUserIdsFromRevision(rev) {
  const ids = [];
  const rb = rev?.required_by;
  if (!Array.isArray(rb)) return ids;
  for (const entry of rb) {
    const p = String(entry?.purpose ?? "").toLowerCase();
    if (p !== "submit" && p !== "approve" && p !== "release") continue;
    const u = entry?.user;
    if (!u) continue;
    const uid = u.id ?? u.user_id ?? u.user_ref;
    if (uid != null && String(uid).trim() !== "") {
      ids.push(String(uid).trim().toLowerCase());
    }
  }
  return ids;
}

/**
 * Unique Submit/Approve/Release user ids from every revision in `revisionData` whose letter family
 * matches `contextRevLabel` (e.g. A- and A1 both contribute). Used for Give Training → user/get-all
 * `sub_aprr_rel_users` when workflow roles differ across same-letter revisions.
 */
export function collectWorkflowActorUserIdsFromSameLetterRevisions(
  revisionData,
  contextRevLabel,
) {
  const out = [];
  const seen = new Set();
  if (!Array.isArray(revisionData) || contextRevLabel == null) return out;
  const ctx = String(contextRevLabel).trim();
  if (!ctx) return out;
  for (const rev of revisionData) {
    const label = rev?.rev != null ? String(rev.rev).trim() : "";
    if (!label || !sameRevisionLetterFamily(label, ctx)) continue;
    for (const uid of collectWorkflowActorUserIdsFromRevision(rev)) {
      const k = String(uid).trim().toLowerCase();
      if (!k || seen.has(k)) continue;
      seen.add(k);
      out.push(k);
    }
  }
  return out;
}

/**
 * Add Module Table of Procedures (live): show "Trained" for the viewer on revision R2 when they are
 * S/A/R on an **older** released/archived revision in the **same letter family** (e.g. User1 on A1,
 * A2 lists only User2 — User1 still sees Trained for the A2 row).
 */
export function isViewerSarOnOlderSameLetterReleasedRevision(
  allRevisionsForPart,
  currentRow,
  viewerUserId,
) {
  if (
    viewerUserId == null ||
    String(viewerUserId).trim() === "" ||
    !Array.isArray(allRevisionsForPart) ||
    !currentRow ||
    typeof currentRow !== "object"
  ) {
    return false;
  }
  const curRevLabel =
    currentRow.rev != null ? String(currentRow.rev).trim() : "";
  const curId = normalizeProcedureRevisionIdKey(
    currentRow.procedureRevisionId ??
      currentRow.procedure_revision_id ??
      currentRow.procedure_revision_ref ??
      "",
  );
  if (!curRevLabel) return false;

  for (const rev of allRevisionsForPart) {
    if (!rev || typeof rev !== "object") continue;
    const label = rev?.rev != null ? String(rev.rev).trim() : "";
    if (!label || !sameRevisionLetterFamily(label, curRevLabel)) continue;
    const st = String(getCurrentStatus(rev) || "").trim().toLowerCase();
    if (st !== "released" && st !== "archived") continue;
    const rid = normalizeProcedureRevisionIdKey(
      rev?.procedure_revision_id ?? rev?.procedure_revision_ref,
    );
    if (curId && rid && rid === curId) continue;
    if (revisionSort({ rev: label }, { rev: curRevLabel }) >= 0) continue;
    if (isRevisionWorkflowActor(rev?.required_by, viewerUserId)) return true;
  }
  return false;
}

function findUserObjectForWorkflowIdInRevision(rev, uidLower) {
  const want = String(uidLower ?? "").trim().toLowerCase();
  if (!want) return null;
  const wantHex = normalizeUuidLikeKey(want);
  const wantIsHex = wantHex && /^[0-9a-f]{32}$/.test(wantHex);
  for (const entry of rev?.required_by || []) {
    const p = String(entry?.purpose ?? "").toLowerCase();
    if (p !== "submit" && p !== "approve" && p !== "release") continue;
    const u = entry?.user;
    if (!u) continue;
    const uid = u.id ?? u.user_id ?? u.user_ref;
    if (uid == null) continue;
    const idStr = String(uid).trim().toLowerCase();
    if (idStr === want) return u;
    if (wantIsHex) {
      const idHex = normalizeUuidLikeKey(idStr);
      if (idHex && idHex === wantHex) return u;
    }
  }
  return null;
}

function findUserObjectInRevisionData(revisionData, uidLower) {
  for (const rev of revisionData || []) {
    const u = findUserObjectForWorkflowIdInRevision(rev, uidLower);
    if (u) return u;
  }
  return null;
}

/**
 * Best date for "training completed" on a released revision when `date_of_training` is absent
 * (workflow / synthetic rows). Prefer `date_released`, then status change timestamps.
 */
export function pickReleasedDateFromRevision(rev) {
  if (!rev || typeof rev !== "object") return null;
  const dr = rev.date_released ?? rev.dateReleased;
  if (dr != null && String(dr).trim() !== "") return dr;
  const sc = Array.isArray(rev.status_changes) ? rev.status_changes : [];
  const releasedLike = sc.filter((s) => {
    const st = String(s?.status_changed_to ?? "").trim().toLowerCase();
    return st === "released" || st === "archived";
  });
  const sorted = [...releasedLike].sort(
    (a, b) =>
      new Date(b?.created_at || b?.updated_at || 0).getTime() -
      new Date(a?.created_at || a?.updated_at || 0).getTime(),
  );
  const hit = sorted[0];
  if (hit) {
    const t = hit.created_at ?? hit.updated_at;
    if (t) return t;
  }
  const latest = sc.find((s) => s?.is_latest);
  if (latest) {
    const t = latest.created_at ?? latest.updated_at;
    if (t) return t;
  }
  return rev.updated_at ?? rev.created_at ?? null;
}

export function findRevisionDataEntryForTrainingLogRow(row, revisionData) {
  const want = normalizeProcedureRevisionIdKey(
    row?.procedure_revision_ref ??
      row?.procedure_revision_id ??
      row?.procedureRevisionId ??
      "",
  );
  if (!want || !Array.isArray(revisionData)) return null;
  return (
    revisionData.find(
      (r) =>
        normalizeProcedureRevisionIdKey(
          r?.procedure_revision_id ?? r?.procedure_revision_ref,
        ) === want,
    ) ?? null
  );
}

function buildSyntheticWorkflowActorTrainingRow(rev, uidLower) {
  const id = rev?.procedure_revision_id ?? rev?.procedure_revision_ref;
  const userObj = findUserObjectForWorkflowIdInRevision(rev, uidLower);
  const name = userObj
    ? `${userObj.first_name || ""} ${userObj.last_name || ""}`.trim()
    : "";
  const dateOfTraining = pickReleasedDateFromRevision(rev);
  return {
    procedure_revision_ref: id,
    procedureRevisionId: id,
    rev: rev?.rev,
    trainee: userObj || { id: uidLower },
    trainee_name: name,
    trainer: userObj || { id: uidLower },
    trainer_name: name,
    trained_to: null,
    is_complete: true,
    date_of_training: dateOfTraining,
  };
}

function buildSyntheticInheritedTrainingRowForRevision({
  procedureRevisionId,
  currentRev,
  traineeIdLower,
  revisionData,
  trainingLogs,
}) {
  const sourceRow = Array.isArray(trainingLogs)
    ? findLatestFullyTrainedSourceTrainingLogRowForInheritedSynthetic({
        trainingLogs,
        revisionData,
        currentProcedureRevisionId: procedureRevisionId,
        currentRev,
        traineeIdLower,
      })
    : null;

  const revEntry =
    revisionData?.find(
      (r) =>
        normalizeProcedureRevisionIdKey(
          r?.procedure_revision_id ?? r?.procedure_revision_ref,
        ) === normalizeProcedureRevisionIdKey(procedureRevisionId),
    ) ?? null;
  const dateOfTraining = pickReleasedDateFromRevision(revEntry);

  if (sourceRow) {
    return {
      procedure_revision_ref: procedureRevisionId,
      procedureRevisionId,
      rev: currentRev,
      trainee: sourceRow.trainee,
      trainee_name: sourceRow.trainee_name,
      trainee_first_name: sourceRow.trainee_first_name,
      trainee_last_name: sourceRow.trainee_last_name,
      trainer: sourceRow.trainer,
      trainer_name: sourceRow.trainer_name,
      trainer_first_name: sourceRow.trainer_first_name,
      trainer_last_name: sourceRow.trainer_last_name,
      /** Carry signatures from the older same-letter Trained log (e.g. A- → A1). */
      trainer_signature: sourceRow.trainer_signature,
      trainee_signature: sourceRow.trainee_signature,
      trained_to: null,
      is_complete: true,
      date_of_training: dateOfTraining,
    };
  }

  const userObj = findUserObjectInRevisionData(revisionData, traineeIdLower);
  const name = userObj
    ? `${userObj.first_name || ""} ${userObj.last_name || ""}`.trim()
    : "";
  const trainerPayload =
    userObj || traineeIdLower
      ? {
          trainer: userObj || { id: traineeIdLower },
          trainer_name: name,
        }
      : {};
  return {
    procedure_revision_ref: procedureRevisionId,
    procedureRevisionId,
    rev: currentRev,
    trainee: userObj || { id: traineeIdLower },
    trainee_name: name,
    /** Same-letter inheritance (e.g. A1 SAR → A2 Trained): show trainee as trainer when not SAR on current rev. */
    ...trainerPayload,
    trained_to: null,
    is_complete: true,
    date_of_training: dateOfTraining,
  };
}

function cloneJsonSafe(v) {
  if (v == null) return v;
  try {
    return JSON.parse(JSON.stringify(v));
  } catch {
    return v;
  }
}

/**
 * Same-letter / older-revision carryover for **partial** training (e.g. A- "Step 2" → show on A1).
 * Copies `trained_to` and trainer/trainee display fields from the source log; revision columns show current.
 */
function buildSyntheticInheritedPartialTrainingRowForRevision({
  procedureRevisionId,
  currentRev,
  sourceRow,
  revisionData,
}) {
  const id = normalizeProcedureRevisionIdKey(procedureRevisionId);
  const revEntry =
    revisionData?.find(
      (r) =>
        normalizeProcedureRevisionIdKey(
          r?.procedure_revision_id ?? r?.procedure_revision_ref,
        ) === id,
    ) ?? null;
  const dateOfTraining =
    sourceRow?.date_of_training ??
    sourceRow?.created_at ??
    pickReleasedDateFromRevision(revEntry);
  return {
    procedure_revision_ref: procedureRevisionId,
    procedureRevisionId,
    rev: currentRev,
    trainee: sourceRow?.trainee,
    trainee_name: sourceRow?.trainee_name,
    trainee_first_name: sourceRow?.trainee_first_name,
    trainee_last_name: sourceRow?.trainee_last_name,
    trainer: sourceRow?.trainer,
    trainer_name: sourceRow?.trainer_name,
    trainer_first_name: sourceRow?.trainer_first_name,
    trainer_last_name: sourceRow?.trainer_last_name,
    trained_to: cloneJsonSafe(sourceRow?.trained_to),
    is_complete: sourceRow?.is_complete,
    is_maintained_outside_system: sourceRow?.is_maintained_outside_system,
    date_of_training: dateOfTraining,
    trainer_signature: sourceRow?.trainer_signature,
    trainee_signature: sourceRow?.trainee_signature,
  };
}

function uuidHex32FromInheritedTid(tid) {
  if (!tid || typeof tid !== "string") return "";
  if (tid.startsWith("name:") || tid.startsWith("email:")) return "";
  const hex = normalizeUuidLikeKey(String(tid).trim().toLowerCase());
  return hex && /^[0-9a-f]{32}$/.test(hex) ? hex : "";
}

/** Match name:/email: keys to a workflow user and return their id as 32-char hex (for deduping). */
function findUuidHexForNameEmailKeyInRevisionData(nameOrEmailKey, revisionData) {
  const isName = nameOrEmailKey.startsWith("name:");
  const isEmail = nameOrEmailKey.startsWith("email:");
  if (!isName && !isEmail) return "";
  const wantName = isName
    ? normalizePersonNameKey(nameOrEmailKey.slice("name:".length))
    : "";
  const wantEmail = isEmail
    ? String(nameOrEmailKey.slice("email:".length)).trim().toLowerCase()
    : "";

  for (const rev of revisionData || []) {
    const rb = rev?.required_by;
    if (!Array.isArray(rb)) continue;
    for (const entry of rb) {
      const u = entry?.user;
      if (!u) continue;
      const uid = u.id ?? u.user_id ?? u.user_ref;
      if (uid == null) continue;
      const uName = normalizePersonNameKey(
        `${u.first_name || ""} ${u.last_name || ""}`.trim(),
      );
      const uEmail = String(u.email || "").trim().toLowerCase();
      if (isName && wantName && uName && uName === wantName) {
        return uuidHex32FromInheritedTid(String(uid).trim().toLowerCase());
      }
      if (isEmail && wantEmail && uEmail && uEmail === wantEmail) {
        return uuidHex32FromInheritedTid(String(uid).trim().toLowerCase());
      }
    }
  }
  return "";
}

/**
 * `buildInheritedFullyTrainedTraineeIds` stores many alias keys per person (uuid, hex, name:…).
 * Synthetic rows must use one representative key each — otherwise duplicate N/A / empty rows appear.
 */
function dedupeInheritedKeysForSyntheticRows(inheritedSet, revisionData) {
  const byHex = new Map();
  for (const tid of inheritedSet || []) {
    if (!tid) continue;
    if (tid.startsWith("name:") || tid.startsWith("email:")) continue;
    const hex = uuidHex32FromInheritedTid(tid);
    if (hex && !byHex.has(hex)) byHex.set(hex, tid);
  }
  const orphans = new Set();
  for (const tid of inheritedSet || []) {
    if (!tid) continue;
    if (!tid.startsWith("name:") && !tid.startsWith("email:")) continue;
    const mappedHex = findUuidHexForNameEmailKeyInRevisionData(tid, revisionData);
    if (mappedHex && byHex.has(mappedHex)) continue;
    if (mappedHex && !byHex.has(mappedHex)) byHex.set(mappedHex, tid);
    else if (!mappedHex) orphans.add(tid);
  }
  return new Set([...byHex.values(), ...orphans]);
}

/** Drop API stubs with no trainee id, name, or trainee object we can display. */
function trainingLogRowHasIdentifiableTrainee(row) {
  if (!row || typeof row !== "object") return false;
  const tid = normalizeTraineeIdFromTrainingLogRow(row);
  if (tid && String(tid).trim()) return true;
  if (row.trainee_name && String(row.trainee_name).trim()) return true;
  if (row.trainee_first_name != null || row.trainee_last_name != null) {
    const n = `${row.trainee_first_name || ""} ${row.trainee_last_name || ""}`.trim();
    if (n) return true;
  }
  const t = row.trainee;
  if (t && typeof t === "object") {
    if (`${t.first_name || ""} ${t.last_name || ""}`.trim()) return true;
    if (t.id != null && String(t.id).trim()) return true;
    if (t.user_id != null && String(t.user_id).trim()) return true;
  }
  if (typeof t === "string" && String(t).trim()) return true;
  return false;
}

/** True when this procedure revision row in `revisionData` is released (or archived). */
export function isProcedureRevisionReleasedInRevisionData(
  revisionData,
  procedureRevisionId,
) {
  const curId = normalizeProcedureRevisionIdKey(procedureRevisionId);
  if (!curId || !Array.isArray(revisionData)) return false;
  const hit = revisionData.find(
    (r) =>
      normalizeProcedureRevisionIdKey(
        r?.procedure_revision_id ?? r?.procedure_revision_ref,
      ) === curId,
  );
  if (!hit) return false;
  const st = String(getCurrentStatus(hit) || "").trim().toLowerCase();
  return st === "released" || st === "archived";
}

/** Letter base from `parseRevision` (e.g. A-, A1, A1a → "A"; B- → "B"). Empty if unparseable. */
function revisionLetterBaseFromRev(rev) {
  const p = parseRevision(rev);
  if (!p || p.base == null || String(p.base).trim() === "") return "";
  return String(p.base).trim();
}

/**
 * Same major letter family (same `base` from `parseRevision`). A- / A1 / A1a share "A"; B- does not
 * carry A-series training forward.
 */
export function sameRevisionLetterFamily(revA, revB) {
  const a = revisionLetterBaseFromRev(revA);
  const b = revisionLetterBaseFromRev(revB);
  if (!a || !b) return false;
  return a === b;
}

/**
 * Revision label for the document route (for same-letter scoping). Prefer `currentRev`; else lookup by id.
 */
export function resolveRevisionRevLabelFromContext(
  revisionData,
  procedureRevisionId,
  currentRev,
) {
  if (currentRev != null && String(currentRev).trim() !== "") {
    return String(currentRev).trim();
  }
  const curId = normalizeProcedureRevisionIdKey(procedureRevisionId);
  if (!curId || !Array.isArray(revisionData)) return "";
  const hit = revisionData.find(
    (r) =>
      normalizeProcedureRevisionIdKey(
        r?.procedure_revision_id ?? r?.procedure_revision_ref,
      ) === curId,
  );
  return hit?.rev != null ? String(hit.rev).trim() : "";
}

/**
 * For each letter base (A, B, …), the newest released/archived revision in that family.
 * Used to build same-letter synthetics for every alphabet on the part (not only the route revision).
 */
function getLatestReleasedRevisionPerLetterFamily(revisionData) {
  const byBase = new Map();
  for (const r of revisionData || []) {
    const st = String(getCurrentStatus(r) || "").trim().toLowerCase();
    if (st !== "released" && st !== "archived") continue;
    const rev = r?.rev != null ? String(r.rev).trim() : "";
    if (!rev || !parseRevision(rev)) continue;
    const base = revisionLetterBaseFromRev(rev);
    if (!base) continue;
    if (!byBase.has(base)) byBase.set(base, []);
    byBase.get(base).push(r);
  }
  const out = new Map();
  for (const [base, revs] of byBase) {
    const sorted = [...revs].sort((a, b) => revisionSort(a, b));
    const last = sorted[sorted.length - 1];
    const id = last?.procedure_revision_id ?? last?.procedure_revision_ref;
    const rev = last?.rev != null ? String(last.rev).trim() : "";
    if (!id || !parseRevision(rev)) continue;
    out.set(base, { procedureRevisionId: id, rev });
  }
  return out;
}

/**
 * All released/archived revisions per letter base (A, B, …), ordered by `revisionSort`.
 * Used to apply same-letter inheritance synthetics for **each** step in the chain (e.g. A-, A1, A1a),
 * not only the newest — so intermediate rows (e.g. A1 “automatically trained”) still appear if the API
 * no longer returns logs tied to that revision id after an amended release.
 */
function getAllReleasedRevisionsSortedPerLetterFamily(revisionData) {
  const byBase = new Map();
  for (const r of revisionData || []) {
    const st = String(getCurrentStatus(r) || "").trim().toLowerCase();
    if (st !== "released" && st !== "archived") continue;
    const rev = r?.rev != null ? String(r.rev).trim() : "";
    if (!rev || !parseRevision(rev)) continue;
    const base = revisionLetterBaseFromRev(rev);
    if (!base) continue;
    if (!byBase.has(base)) byBase.set(base, []);
    byBase.get(base).push(r);
  }
  const out = new Map();
  for (const [base, revs] of byBase) {
    out.set(base, [...revs].sort((a, b) => revisionSort(a, b)));
  }
  return out;
}

/**
 * Newest released or archived revision **in the same letter family** as `contextRev` (`revisionSort`
 * within that family). When `contextRev` is empty, falls back to global newest (legacy).
 * Synthetic rows key off this when the route is a draft (e.g. A1a → latest released A* = A1).
 */
export function findLatestReleasedProcedureRevision(revisionData, contextRev = null) {
  if (!Array.isArray(revisionData) || revisionData.length === 0) return null;
  const ctx = contextRev != null ? String(contextRev).trim() : "";
  if (!ctx) return null;

  const released = revisionData.filter((r) => {
    const st = String(getCurrentStatus(r) || "").trim().toLowerCase();
    return st === "released" || st === "archived";
  });
  if (!released.length) return null;

  const pool = released.filter((r) => {
    const rr = r?.rev != null ? String(r.rev).trim() : "";
    return rr && sameRevisionLetterFamily(rr, ctx);
  });
  if (!pool.length) return null;

  const sorted = [...pool].sort((a, b) => revisionSort(a, b));
  const last = sorted[sorted.length - 1];
  const id = last?.procedure_revision_id ?? last?.procedure_revision_ref;
  const rev = last?.rev != null ? String(last.rev).trim() : "";
  if (!id || !parseRevision(rev)) return null;
  return { procedureRevisionId: id, rev };
}

/**
 * Procedure revision ids for same-letter revisions strictly before `currentRev` (`revisionSort`).
 * Used for inherited Trained / synthetic display rows.
 */
function getOlderSameLetterProcedureRevisionIdsBefore(
  revisionData,
  currentProcedureRevisionId,
  currentRev,
) {
  const out = new Set();
  const curId = normalizeProcedureRevisionIdKey(currentProcedureRevisionId);
  if (!curId || !Array.isArray(revisionData)) return out;

  let resolvedCurRev =
    currentRev != null && String(currentRev).trim() !== ""
      ? String(currentRev).trim()
      : "";
  if (!resolvedCurRev) {
    const hit = revisionData.find(
      (r) =>
        normalizeProcedureRevisionIdKey(
          r?.procedure_revision_id ?? r?.procedure_revision_ref,
        ) === curId,
    );
    resolvedCurRev = hit?.rev != null ? String(hit.rev).trim() : "";
  }

  if (!parseRevision(resolvedCurRev)) return out;

  const revById = new Map();
  for (const r of revisionData) {
    const id = normalizeProcedureRevisionIdKey(
      r?.procedure_revision_id ?? r?.procedure_revision_ref,
    );
    if (!id) continue;
    revById.set(id, {
      rev: r?.rev != null ? String(r.rev).trim() : "",
    });
  }

  for (const [id, meta] of revById) {
    if (id === curId) continue;
    if (!parseRevision(meta.rev)) continue;
    if (!sameRevisionLetterFamily(meta.rev, resolvedCurRev)) continue;
    if (revisionSort({ rev: meta.rev }, { rev: resolvedCurRev }) < 0) {
      out.add(id);
    }
  }
  return out;
}

/**
 * Deduped API rows plus synthetic rows for workflow actors missing from GET, and synthetic rows for
 * trainees who inherit "Trained" on the current revision (from older released revs per `revisionSort`)
 * but have no log yet for the current id.
 *
 * **All procedure revisions** with training data for this part are included in one table (merged GET).
 * Synthetics: for **each** letter family (A, B, …), apply same-letter carryover for **every**
 * released revision in that family (A-, A1, A1a, …), not only the newest. Otherwise, after an amended
 * release (e.g. A1a), logs that only existed under A1 disappear from the API and the A1 “automatic”
 * rows could not be reconstructed.
 */
export function buildTrainingLogDisplayRows({
  trainingLogs,
  revisionData,
  procedureRevisionId: routeProcedureRevisionId,
  currentRev: routeCurrentRev,
}) {
  void routeProcedureRevisionId;
  void routeCurrentRev;
  const base = dedupeTrainingLogsLatestPerTraineePerRevision(
    trainingLogs || [],
  ).filter(trainingLogRowHasIdentifiableTrainee);

  /** Full merged history for the part — do not narrow to the route revision or draft rows hide prior revs. */
  const baseRows = base;

  const seen = new Set();
  for (const r of baseRows) {
    const pr = normalizeProcedureRevisionDedupeKey(r);
    const idOnly = normalizeTraineeIdFromTrainingLogRowIdOnly(r);
    const idNorm = idOnly ? normalizeUuidLikeKey(idOnly) : "";
    if (pr && idNorm) {
      seen.add(`${pr}|${idNorm}`);
      continue;
    }
    const tRaw = normalizeTraineeIdFromTrainingLogRow(r);
    const t = normalizeUuidLikeKey(tRaw);
    if (pr && t) seen.add(`${pr}|${t}`);
  }

  const extras = [];

  const latestByLetter = getLatestReleasedRevisionPerLetterFamily(revisionData);
  const releasedByLetter = getAllReleasedRevisionsSortedPerLetterFamily(revisionData);
  const partialKeysByLetter = new Map();

  for (const [letterBase, revsInFamily] of releasedByLetter) {
    if (!revsInFamily.length) continue;

    const latestEntry = revsInFamily[revsInFamily.length - 1];
    const latestId =
      latestEntry?.procedure_revision_id ?? latestEntry?.procedure_revision_ref;
    const latestRev =
      latestEntry?.rev != null ? String(latestEntry.rev).trim() : "";

    const partialCarryoverLatest =
      latestId && latestRev
        ? buildInheritedPartialTrainingCarryoverForCurrentRevision({
            trainingLogs: base,
            revisionData,
            procedureRevisionId: latestId,
            currentRev: latestRev,
          })
        : { uidKeys: new Set(), rows: [] };
    partialKeysByLetter.set(letterBase, partialCarryoverLatest.uidKeys);

    for (const revEntry of revsInFamily) {
      const effectiveId =
        revEntry?.procedure_revision_id ?? revEntry?.procedure_revision_ref;
      const effectiveRev =
        revEntry?.rev != null ? String(revEntry.rev).trim() : "";
      if (!effectiveId || !parseRevision(effectiveRev)) continue;

      const partialCarryover =
        buildInheritedPartialTrainingCarryoverForCurrentRevision({
          trainingLogs: base,
          revisionData,
          procedureRevisionId: effectiveId,
          currentRev: effectiveRev,
        });

      const inherited = buildInheritedFullyTrainedTraineeIds({
        trainingLogs: base,
        revisionData,
        currentProcedureRevisionId: effectiveId,
        currentRev: effectiveRev,
      });

      const curPrKey = normalizeProcedureRevisionDedupeKey({
        procedure_revision_ref: effectiveId,
        rev: effectiveRev,
        procedureRevisionId: effectiveId,
        procedure_revision_id: effectiveId,
      });

      if (!curPrKey) continue;

      const syntheticInheritedIds = dedupeInheritedKeysForSyntheticRows(
        inherited,
        revisionData,
      );
      for (const tid of syntheticInheritedIds) {
        if (!tid) continue;
        const uidKey = normalizeUuidLikeKey(tid);
        const key = `${curPrKey}|${uidKey}`;
        if (seen.has(key)) continue;
        seen.add(key);
        extras.push(
          buildSyntheticInheritedTrainingRowForRevision({
            procedureRevisionId: effectiveId,
            currentRev: effectiveRev,
            traineeIdLower: tid,
            revisionData,
            trainingLogs: base,
          }),
        );
      }
      for (const synth of partialCarryover.rows) {
        const idOnly = normalizeTraineeIdFromTrainingLogRowIdOnly(synth);
        const uidKey = idOnly ? normalizeUuidLikeKey(idOnly) : "";
        const key = uidKey ? `${curPrKey}|${uidKey}` : "";
        if (!key) continue;
        if (seen.has(key)) continue;
        seen.add(key);
        extras.push(synth);
      }
    }
  }

  for (const rev of revisionData || []) {
    const st = String(getCurrentStatus(rev) || "").trim().toLowerCase();
    if (st !== "released" && st !== "archived") continue;
    const prKey = normalizeProcedureRevisionDedupeKey({
      procedure_revision_ref:
        rev?.procedure_revision_id ?? rev?.procedure_revision_ref,
      rev: rev?.rev,
      procedureRevisionId: rev?.procedure_revision_id,
      procedure_revision_id: rev?.procedure_revision_id,
    });
    if (!prKey) continue;
    const revIdNorm = normalizeProcedureRevisionIdKey(
      rev?.procedure_revision_id ?? rev?.procedure_revision_ref,
    );
    const revLabel = rev?.rev != null ? String(rev.rev).trim() : "";
    const letterBase = revisionLetterBaseFromRev(revLabel);
    const latestInFamily = letterBase ? latestByLetter.get(letterBase) : null;
    const latestNorm = latestInFamily
      ? normalizeProcedureRevisionIdKey(latestInFamily.procedureRevisionId)
      : "";
    const partialKeysForFamily =
      (letterBase && partialKeysByLetter.get(letterBase)) || new Set();

    for (const uid of collectWorkflowActorUserIdsFromRevision(rev)) {
      const uidKey = normalizeUuidLikeKey(uid);
      const key = `${prKey}|${uidKey}`;
      if (seen.has(key)) continue;
      if (
        latestNorm &&
        revIdNorm === latestNorm &&
        partialKeysForFamily.size > 0 &&
        partialKeysForFamily.has(uidKey)
      ) {
        continue;
      }
      seen.add(key);
      extras.push(buildSyntheticWorkflowActorTrainingRow(rev, uid));
    }
  }

  if (!extras.length) return baseRows;
  return dedupeTrainingLogsLatestPerTraineePerRevision([...baseRows, ...extras]);
}

/**
 * Prefer `trained_to` when present — GET may still expose stale training_status (e.g. "Partially Trained")
 * after a full procedure completion was saved with selection_scope "procedure".
 */
export const resolveTrainingStatusLabelFromLog = (log) => {
  if (!log || typeof log !== "object") return "Not Trained";
  /** Workflow actors (Submitter / Approver / Releaser): complete with no step payload. */
  if (isTopLevelTrainingLogComplete(log) && !hasUsableTrainedTo(log)) {
    return "Trained";
  }
  if (hasUsableTrainedTo(log)) {
    const computed = computeTrainingStatusFromLatestLog(log);
    // Final submitter / approver / releaser rows may have `is_complete` while `trained_to.steps`
    // is empty or stale; still treat as Trained for same-letter revision inheritance + table display.
    if (isTopLevelTrainingLogComplete(log) && computed === "Not Trained") {
      return "Trained";
    }
    return computed;
  }
  const directKeys = [
    "status",
    "training_status",
    "trainingStatus",
    "viewer_training_status",
    "overall_training_status",
  ];
  for (const k of directKeys) {
    const v = log[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return computeTrainingStatusFromLatestLog(log);
};

/** True when this row should carry forward as partial training on a newer revision (not fully Trained / Not Trained). */
function isInheritedPartialTrainingStatusForLog(row) {
  if (!row || !hasUsableTrainedTo(row)) return false;
  const label = resolveTrainingStatusLabelFromLog(row);
  const l = String(label).trim().toLowerCase();
  if (l === "trained") return false;
  if (l === "not trained") return false;
  return true;
}

/** Stable trainee key for matching log rows (GET payloads vary by field names). */
export function normalizeTraineeIdFromTrainingLogRow(row) {
  const raw =
    row?.trainee?.id ??
    row?.trainee?.user_id ??
    row?.trainee?.user_ref ??
    row?.trainee_id ??
    row?.trainee_ref ??
    row?.user_ref ??
    (typeof row?.trainee === "string" ? row.trainee : null) ??
    row?.trainee_name ??
    null;
  if (raw != null && String(raw).trim() !== "") {
    return String(raw).trim().toLowerCase();
  }
  const email =
    row?.trainee?.email ??
    row?.trainee_email ??
    row?.email ??
    null;
  if (email != null && String(email).trim() !== "") {
    return `email:${String(email).trim().toLowerCase()}`;
  }
  const fullName =
    `${row?.trainee?.first_name || ""} ${row?.trainee?.last_name || ""}`.trim() ||
    String(row?.trainee_name || "").trim();
  if (fullName) {
    return `name:${fullName.toLowerCase().replace(/\s+/g, " ")}`;
  }
  return "";
}

/**
 * One row per (trainee, procedure revision): same user on A1 and A2 → two rows;
 * same user twice on A1 → one row (latest by date_of_training → created_at → updated_at).
 */
export function dedupeTrainingLogsLatestPerTraineePerRevision(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const toMs = (v) => {
    if (!v) return 0;
    const d = new Date(v);
    const t = d.getTime();
    return Number.isFinite(t) ? t : 0;
  };
  const rowSortKey = (row) => {
    const dateMs = toMs(row?.date_of_training);
    const createdMs = toMs(row?.created_at);
    const updatedMs = toMs(row?.updated_at);
    return [dateMs, createdMs, updatedMs];
  };
  const compareDesc = (a, b) => {
    const ka = rowSortKey(a);
    const kb = rowSortKey(b);
    for (let i = 0; i < ka.length; i += 1) {
      if (ka[i] !== kb[i]) return kb[i] - ka[i];
    }
    /** Same timestamp: prefer real logs over workflow stubs (`training_log_id` === `procedure_revision_ref`). */
    const spA = isSpuriousRevisionKeyedTrainingLogRow(a) ? 1 : 0;
    const spB = isSpuriousRevisionKeyedTrainingLogRow(b) ? 1 : 0;
    return spA - spB;
  };
  const sorted = [...rows].sort(compareDesc);
  const seen = new Set();
  const out = [];
  for (const r of sorted) {
    const pr = normalizeProcedureRevisionDedupeKey(r);
    if (!pr) continue;

    const idOnly = normalizeTraineeIdFromTrainingLogRowIdOnly(r);
    const idNorm = idOnly ? normalizeUuidLikeKey(idOnly) : "";
    if (idNorm) {
      const key = `${pr}|${idNorm}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(r);
      continue;
    }

    const tRaw = normalizeTraineeIdFromTrainingLogRow(r);
    const t = normalizeUuidLikeKey(tRaw);
    if (t) {
      const key = `${pr}|${t}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(r);
    }
  }

  return collapseDuplicateTraineePerRevisionByDisplayName(out, compareDesc);
}

/**
 * Keys used to match a training-log row to `buildInheritedFullyTrainedTraineeIds` (uuid, hex, name:…).
 */
function collectTraineeKeysForInheritedMatch(row) {
  const keys = new Set();
  const tid = normalizeTraineeIdFromTrainingLogRow(row);
  if (tid) keys.add(tid);
  const idOnly = normalizeTraineeIdFromTrainingLogRowIdOnly(row);
  if (idOnly) {
    const k = String(idOnly).trim().toLowerCase();
    keys.add(k);
    const hex = normalizeUuidLikeKey(idOnly);
    if (hex) keys.add(hex);
  }
  const dn = normalizeTraineeDisplayNameForDedupe(row);
  if (dn) keys.add(`name:${dn}`);
  return keys;
}

function trainingLogRowMatchesInheritedTid(row, tid) {
  if (!row || tid == null || String(tid).trim() === "") return false;
  const want = String(tid).trim().toLowerCase();
  for (const k of collectTraineeKeysForInheritedMatch(row)) {
    if (!k) continue;
    if (String(k).toLowerCase() === want) return true;
  }
  const wantHex = uuidHex32FromInheritedTid(want);
  if (wantHex) {
    for (const k of collectTraineeKeysForInheritedMatch(row)) {
      const h = uuidHex32FromInheritedTid(k);
      if (h && h === wantHex) return true;
    }
  }
  return false;
}

function revisionRevStringForProcedureId(revisionData, prIdNorm) {
  const hit = revisionData?.find(
    (r) =>
      normalizeProcedureRevisionIdKey(
        r?.procedure_revision_id ?? r?.procedure_revision_ref,
      ) === prIdNorm,
  );
  return hit?.rev != null ? String(hit.rev).trim() : "";
}

/**
 * Latest same-letter older revision’s Trained log for this trainee — used to copy trainer/trainee
 * names onto inherited “Trained” synthetics (non-SAR trainees are not in `required_by`).
 */
function findLatestFullyTrainedSourceTrainingLogRowForInheritedSynthetic({
  trainingLogs,
  revisionData,
  currentProcedureRevisionId,
  currentRev,
  traineeIdLower,
}) {
  const olderIds = getOlderSameLetterProcedureRevisionIdsBefore(
    revisionData,
    currentProcedureRevisionId,
    currentRev,
  );
  if (!olderIds.size) return null;

  const candidates = [];
  for (const row of trainingLogs || []) {
    if (!row || isSpuriousRevisionKeyedTrainingLogRow(row)) continue;
    const pr = getProcedureRevisionLookupKeyFromRow(row);
    if (!pr || !olderIds.has(pr)) continue;
    const label = resolveTrainingStatusLabelFromLog(row);
    if (String(label).trim().toLowerCase() !== "trained") continue;
    if (!trainingLogRowMatchesInheritedTid(row, traineeIdLower)) continue;
    candidates.push(row);
  }
  if (!candidates.length) return null;

  candidates.sort((a, b) => {
    const revA = revisionRevStringForProcedureId(
      revisionData,
      getProcedureRevisionLookupKeyFromRow(a),
    );
    const revB = revisionRevStringForProcedureId(
      revisionData,
      getProcedureRevisionLookupKeyFromRow(b),
    );
    return revisionSort({ rev: revA }, { rev: revB });
  });
  return candidates[candidates.length - 1];
}

function groupKeyForTraineeRow(row) {
  const idOnly = normalizeTraineeIdFromTrainingLogRowIdOnly(row);
  if (idOnly) return `id:${normalizeUuidLikeKey(idOnly)}`;
  const t = normalizeTraineeIdFromTrainingLogRow(row);
  return t ? `t:${t}` : "";
}

/** When the same trainee has partial logs on multiple older revs, keep the row from the newest older revision. */
function pickLatestOlderPartialRowPerTrainee(rows, revById) {
  const m = new Map();
  for (const row of rows) {
    const pr = getProcedureRevisionLookupKeyFromRow(row);
    if (!pr || !revById.has(pr)) continue;
    const gk = groupKeyForTraineeRow(row);
    if (!gk) continue;
    const prev = m.get(gk);
    if (!prev) {
      m.set(gk, row);
      continue;
    }
    const prevPr = getProcedureRevisionLookupKeyFromRow(prev);
    const prevRev = revById.get(prevPr)?.rev;
    const curRevStr = revById.get(pr)?.rev;
    if (revisionSort({ rev: prevRev }, { rev: curRevStr }) < 0) {
      m.set(gk, row);
    }
  }
  return [...m.values()];
}

/**
 * For a released current revision, build synthetic rows (and matching uid keys) so trainees who were
 * **partially** trained on an older released revision appear on the current revision with the same
 * `trained_to` payload (e.g. A- "Step 2" → A1). `uidKeys` suppresses SAR-only workflow synthetics that
 * would otherwise show "Trained" for the same person on the current revision.
 */
function buildInheritedPartialTrainingCarryoverForCurrentRevision({
  trainingLogs,
  revisionData,
  procedureRevisionId,
  currentRev,
}) {
  const outRows = [];
  const uidKeys = new Set();
  const curId = normalizeProcedureRevisionIdKey(procedureRevisionId);
  if (!curId || !Array.isArray(revisionData)) {
    return { uidKeys, rows: outRows };
  }

  if (!isProcedureRevisionReleasedInRevisionData(revisionData, procedureRevisionId)) {
    return { uidKeys, rows: outRows };
  }

  let resolvedCurRev =
    currentRev != null && String(currentRev).trim() !== ""
      ? String(currentRev).trim()
      : "";
  if (!resolvedCurRev) {
    const hit = revisionData.find(
      (r) =>
        normalizeProcedureRevisionIdKey(
          r?.procedure_revision_id ?? r?.procedure_revision_ref,
        ) === curId,
    );
    resolvedCurRev = hit?.rev != null ? String(hit.rev).trim() : "";
  }

  if (!parseRevision(resolvedCurRev)) {
    return { uidKeys, rows: outRows };
  }

  const revById = new Map();
  for (const r of revisionData) {
    const id = normalizeProcedureRevisionIdKey(
      r?.procedure_revision_id ?? r?.procedure_revision_ref,
    );
    if (!id) continue;
    revById.set(id, {
      rev: r?.rev != null ? String(r.rev).trim() : "",
    });
  }

  const olderBeforeCurrentIds = new Set();
  for (const [id, meta] of revById) {
    if (id === curId) continue;
    if (!parseRevision(meta.rev)) continue;
    if (!sameRevisionLetterFamily(meta.rev, resolvedCurRev)) continue;
    if (revisionSort({ rev: meta.rev }, { rev: resolvedCurRev }) < 0) {
      olderBeforeCurrentIds.add(id);
    }
  }
  if (olderBeforeCurrentIds.size === 0) {
    return { uidKeys, rows: outRows };
  }

  const candidates = [];
  for (const row of trainingLogs || []) {
    if (!trainingLogRowHasIdentifiableTrainee(row)) continue;
    const pr = getProcedureRevisionLookupKeyFromRow(row);
    if (!olderBeforeCurrentIds.has(pr)) continue;
    if (isSpuriousRevisionKeyedTrainingLogRow(row)) continue;
    if (!isInheritedPartialTrainingStatusForLog(row)) continue;
    candidates.push(row);
  }
  if (candidates.length === 0) {
    return { uidKeys, rows: outRows };
  }

  const latestPerTrainee = pickLatestOlderPartialRowPerTrainee(candidates, revById);

  for (const sourceRow of latestPerTrainee) {
    const synth = buildSyntheticInheritedPartialTrainingRowForRevision({
      procedureRevisionId,
      currentRev: resolvedCurRev,
      sourceRow,
      revisionData,
    });
    outRows.push(synth);
    for (const k of collectTraineeKeysForInheritedMatch(sourceRow)) {
      const hex = uuidHex32FromInheritedTid(k);
      if (hex) uidKeys.add(hex);
      if (!k.startsWith("name:") && !k.startsWith("email:")) {
        uidKeys.add(normalizeUuidLikeKey(k));
      }
    }
  }
  return { uidKeys, rows: outRows };
}

/** True if this row's trainee appears in the inherited-Trained set (handles id vs name: key mismatch). */
export function isTraineeInInheritedFullyTrainedSet(row, inheritedSet) {
  if (!inheritedSet || inheritedSet.size === 0 || !row) return false;
  for (const k of collectTraineeKeysForInheritedMatch(row)) {
    if (k && inheritedSet.has(k)) return true;
  }
  return false;
}

/**
 * Matches `DocumentAppBar` `effectiveViewerTrainingStatusLabel`: when GET returns "Not Trained" for the
 * current revision but same-letter inheritance marks the viewer as fully trained on an older revision
 * (e.g. trained on A- → show Trained on A1), return "Trained". Used by Table of Procedures so it stays
 * consistent with the revision screen header.
 */
export function resolveEffectiveViewerTrainingStatusLabel({
  baseStatus,
  authUserId,
  procedureRevisionId,
  revLabel,
  revisionData,
  trainingLogs,
}) {
  const base = String(baseStatus ?? "").trim();
  if (authUserId == null || String(authUserId).trim() === "") {
    return baseStatus;
  }
  if (base !== "Not Trained") return baseStatus;
  if (/\bStep\b/i.test(base)) return baseStatus;

  if (!Array.isArray(revisionData) || revisionData.length === 0) {
    return baseStatus;
  }

  const letterContextRev =
    revLabel != null && String(revLabel).trim() !== ""
      ? String(revLabel).trim()
      : resolveRevisionRevLabelFromContext(
          revisionData,
          procedureRevisionId,
          null,
        );
  const eff = findLatestReleasedProcedureRevision(
    revisionData,
    letterContextRev,
  );
  const inherited = buildInheritedFullyTrainedTraineeIds({
    trainingLogs: trainingLogs || [],
    revisionData,
    currentProcedureRevisionId: eff?.procedureRevisionId ?? procedureRevisionId,
    currentRev: eff?.rev ?? revLabel,
  });
  const probe = { trainee: { id: authUserId } };
  if (isTraineeInInheritedFullyTrainedSet(probe, inherited)) {
    return "Trained";
  }
  return baseStatus;
}

/**
 * Trainee ids who were fully Trained on any **same-letter** older procedure revision (`revisionSort`).
 * Training from a prior letter (e.g. A) does not carry forward when the current revision is a new
 * letter (e.g. B); only SAR + logs on that letter apply.
 *
 * "Older" uses `revisionSort`, not `created_at` (which can mis-order A- vs A1 or break when the
 * current row is missing from the list).
 */
export function buildInheritedFullyTrainedTraineeIds({
  trainingLogs,
  revisionData,
  currentProcedureRevisionId,
  currentRev,
}) {
  const out = new Set();
  const curId = normalizeProcedureRevisionIdKey(currentProcedureRevisionId);
  if (!curId || !Array.isArray(revisionData)) return out;

  if (!isProcedureRevisionReleasedInRevisionData(revisionData, currentProcedureRevisionId)) {
    return out;
  }

  const olderBeforeCurrentIds = getOlderSameLetterProcedureRevisionIdsBefore(
    revisionData,
    currentProcedureRevisionId,
    currentRev,
  );
  if (olderBeforeCurrentIds.size === 0) return out;

  for (const row of trainingLogs || []) {
    const pr = getProcedureRevisionLookupKeyFromRow(row);
    if (!olderBeforeCurrentIds.has(pr)) continue;
    const label = resolveTrainingStatusLabelFromLog(row);
    if (String(label).trim().toLowerCase() !== "trained") continue;
    for (const k of collectTraineeKeysForInheritedMatch(row)) {
      if (k) out.add(k);
    }
  }

  /** Submitter / Approver / Releaser on a released older revision are Trained for that revision. */
  for (const r of revisionData) {
    const id = normalizeProcedureRevisionIdKey(
      r?.procedure_revision_id ?? r?.procedure_revision_ref,
    );
    if (!id || !olderBeforeCurrentIds.has(id)) continue;
    const st = String(getCurrentStatus(r) || "").trim().toLowerCase();
    if (st !== "released" && st !== "archived") continue;
    const revIdNorm = normalizeProcedureRevisionIdKey(id);
    for (const uid of collectWorkflowActorUserIdsFromRevision(r)) {
      if (!uid) continue;
      /** Do not inherit SAR "Trained" when the same person has a manual `trained_to` log on that revision that is not fully Trained (e.g. partial step training). */
      let manualNonFullyTrained = false;
      for (const row of trainingLogs || []) {
        if (!row || isSpuriousRevisionKeyedTrainingLogRow(row)) continue;
        const pr = getProcedureRevisionLookupKeyFromRow(row);
        if (!pr || normalizeProcedureRevisionIdKey(pr) !== revIdNorm) continue;
        if (!trainingLogRowMatchesWorkflowUserId(row, uid)) continue;
        if (!hasUsableTrainedTo(row)) continue;
        const label = resolveTrainingStatusLabelFromLog(row);
        if (String(label).trim().toLowerCase() !== "trained") {
          manualNonFullyTrained = true;
          break;
        }
      }
      if (manualNonFullyTrained) continue;
      out.add(uid);
      const u = findUserObjectForWorkflowIdInRevision(r, uid);
      if (u) {
        const nk = normalizePersonNameKey(
          `${u.first_name || ""} ${u.last_name || ""}`.trim(),
        );
        if (nk) out.add(`name:${nk}`);
      }
    }
  }
  return out;
}

/**
 * Fetches training logs for a procedure revision and returns a display label
 * (same logic as AddModule "Table of Procedures").
 */
export async function fetchTrainingStatusLabelForProcedureRevision(
  dispatch,
  procedureRevisionId,
  partRef,
) {
  const key = normalizeProcedureRevisionIdKey(procedureRevisionId);
  if (!key) return { procedureRevisionId: "", status: "Not Trained" };

  try {
    const { key: k, sorted } = await fetchSortedTrainingLogsForProcedureRevision(
      dispatch,
      procedureRevisionId,
      partRef,
    );
    if (!sorted.length) {
      return { procedureRevisionId: k || key, status: "Not Trained" };
    }

    const logToUse = pickTrainingLogRowForStatusLabelUnscoped(sorted) || {};
    const status = resolveTrainingStatusLabelFromLog(logToUse);

    return { procedureRevisionId: k || key, status: String(status) };
  } catch (e) {
    console.error(
      "Failed to fetch training status for procedureRevisionId:",
      procedureRevisionId,
      e,
    );
    return { procedureRevisionId: key, status: "Not Trained" };
  }
}

export async function fetchTrainingStatusLabelForProcedureRevisionForTrainee(
  dispatch,
  procedureRevisionId,
  traineeId,
  partRef,
) {
  const key = normalizeProcedureRevisionIdKey(procedureRevisionId);
  if (!key) return { procedureRevisionId: "", status: "Not Trained" };
  if (traineeId == null || String(traineeId).trim() === "") {
    return fetchTrainingStatusLabelForProcedureRevision(
      dispatch,
      procedureRevisionId,
      partRef,
    );
  }

  try {
    const { key: k, sorted } = await fetchSortedTrainingLogsForProcedureRevisionForTrainee(
      dispatch,
      procedureRevisionId,
      traineeId,
      partRef,
    );
    if (!sorted.length) {
      return { procedureRevisionId: k || key, status: "Not Trained" };
    }

    const pool = poolTrainingLogsForTrainee(sorted, traineeId);
    if (!pool.length) {
      return { procedureRevisionId: k || key, status: "Not Trained" };
    }
    const logToUse = pickTrainingLogRowForStatusLabel(pool) || {};
    const status = resolveTrainingStatusLabelFromLog(logToUse);
    return { procedureRevisionId: k || key, status: String(status) };
  } catch (e) {
    console.error(
      "Failed to fetch trainee training status for procedureRevisionId:",
      procedureRevisionId,
      traineeId,
      e,
    );
    return { procedureRevisionId: key, status: "Not Trained" };
  }
}
